import React, {
    useMemo,
    FocusEventHandler,
    memo,
    FormEvent,
    useState,
    forwardRef,
    useEffect,
    Suspense,
    useCallback,
    ChangeEvent,
    SyntheticEvent,
    ReactNode,
    useLayoutEffect,
    useRef,
} from 'react';
import uniq from 'lodash/uniq';
import omit from 'lodash/omit';
import {
    Autocomplete,
    Box,
    FormControlLabel,
    MenuItem,
    Switch,
    TextField,
    FormGroup,
    InputBaseComponentProps,
    useTheme,
    TextFieldProps,
    styled,
    Typography,
    Button,
    Tabs,
    Tab,
    CircularProgress,
} from '@mui/material';
import { useForm, SubmitHandler, Controller } from 'react-hook-form';
import { TypeOf, z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { js_beautify } from 'js-beautify';
import { IArgumentMetadata, ArgTypes, ArgScopes, isCategoricalMetadata } from '@tymely/atoms';
import {
    useArgExtractorsQuery,
    useArgumentSchemaQuery,
    useArgumentsMetadataQuery,
    useCreateArgumentMetadataMutation,
    useDTypeHintsQuery,
    useLlmExperimentSchemaQuery,
} from '@tymely/services';
import { BaseModal, IconButton, IconButtonProps } from '@tymely/components';
import ResizeIcon from '@tymely/components/Icons/Resize';
import { CodeEditor } from '@tymely/components/CodeEditor';
import { TemplateEditor, TemplateEditorModal } from '@tymely/features';
import { EditPromptIconButton } from '@tymely/components/EditPromptIconButton';
import { TemplateEditorModeProvider } from '@tymely/features/TemplateEditor/components/TemplateEditorModeContext';
import { stopProgagation } from '@tymely/utils';
import { safeJsonParse } from 'src/utils/json';

import { PromptExperimentModal } from '../PromptExperimentModal/PromptExperimentModal';

export const defaultArgMetadata: IArgumentMetadata = {
    id: -1,
    title: '',
    description: '',
    extractor_name: 'user_input',
    dtype: 'str',
    arg_type: 'TEXT_ARGUMENT',
    scope: 'COMMENT',
    is_list: false,
    is_ticket_arg: false,
    unspecifiable: true,
    unknowable: false,
    dependencies: [],
    name: '',
    params: {
        threshold: null,
    },
    additional_data: {},
    created_date: new Date(),
};

const jsonValidator = () =>
    z.nullable(z.string()).refine(
        (str) => {
            try {
                return str ? JSON.parse(str) : true;
            } catch (_error) {
                return false;
            }
        },
        { message: 'Invalid JSON' },
    );

const formSchema = z.object({
    name: z
        .string()
        .min(1, { message: 'Name is required' })
        .regex(/^[\w]+$/, { message: 'Name must be alphanumeric' }),
    title: z.string().min(1, { message: 'Title is required' }),
    description: z
        .string()
        .min(1, { message: 'Description is required' })
        .max(256, { message: 'Description is too long (256 chars max)' }),
    threshold: z.number().min(0).optional(),
    dtype: z.string().min(1, { message: 'DType is required' }),
    arg_type: z.enum(ArgTypes, { required_error: 'Argument Type is required' }),
    scope: z.enum(ArgScopes, { required_error: 'Scope is required' }),
    extractor_name: z.string().min(1, { message: 'Extractor is required' }),
    options: z
        .nullable(
            z.object({
                categories: jsonValidator().nullable(),
                dynamic_categories: jsonValidator().nullable(),
                neitherable: z.boolean().nullable(),
                group_by_label: z.boolean().nullable(),
            }),
        )
        .optional(),
    unspecifiable: z.boolean(),
    unknowable: z.boolean(),
    params: jsonValidator(),
    additional_data: z
        .object({
            ttl_minutes: z.union([
                z.number().min(0).optional(),
                z
                    .string()
                    .transform((val) => (val ? Number(val) : undefined))
                    .refine((num) => num === undefined || Number(num) >= 0, 'Expected non-negative number')
                    .optional(),
            ]),
            cache_missing_value: z.boolean().optional(),
            show_description: z.boolean().optional(),
        })
        .optional(),
});

type FormInput = TypeOf<typeof formSchema>;

const JsonEditor = forwardRef<
    HTMLInputElement,
    InputBaseComponentProps & {
        name: string;
        disabled?: boolean;
        jinjaFields?: boolean;
    }
>((props, ref) => {
    const theme = useTheme();
    const { onChange } = props;
    const [editedString, setEditedString] = useState<string | undefined>();
    const replaceValue = useRef<((value: string) => void) | undefined>();
    const editor = useMemo(() => ({ value: '', nodeName: 'INPUT' }) as HTMLInputElement, []);

    useEffect(() => {
        if (typeof ref === 'function') {
            ref(editor);
        }
    }, []);

    const editStringCallback = (value: string, replace: (newValue: string) => void) => {
        replaceValue.current = replace;
        setEditedString(value);
    };

    return (
        <Box width="100%" mb={1}>
            <Suspense>
                <CodeEditor
                    code={editor.value}
                    language="json"
                    onChange={
                        props.disabled
                            ? undefined
                            : (value: string) => {
                                  onChange?.({
                                      target: { name: props.name, value },
                                  } as unknown as FormEvent<HTMLInputElement>);
                              }
                    }
                    editStringCallback={props.jinjaFields ? editStringCallback : undefined}
                    editorProps={{
                        maxHeight: theme.spacing(22),
                        onBlur: props.onBlur as unknown as FocusEventHandler<HTMLInputElement>,
                    }}
                />
            </Suspense>
            {editedString !== undefined && (
                <TemplateEditorModal
                    open
                    template={editedString}
                    onClose={() => {
                        setEditedString(undefined);
                        replaceValue.current = undefined;
                    }}
                    onSave={(value) => replaceValue.current?.(value)}
                />
            )}
        </Box>
    );
});

JsonEditor.displayName = 'JsonEditor';

const ExpandButton = styled((props: IconButtonProps) => (
    <IconButton {...props}>
        <ResizeIcon style={{ transform: 'rotate(45deg)' }} />
    </IconButton>
))(
    ({ theme }) => `
    width: ${theme.spacing(3)};
    height: ${theme.spacing(3)};
    position: absolute;
    top: 0;
    right: 0;
    border: 1px solid var(--ty-palette-divider);
`,
);

const CodeEditorModal = memo(
    (props: {
        open: boolean;
        name?: string;
        title: string;
        code: string;
        loading?: boolean;
        jinjaField?: string;
        onClose: () => void;
        onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
    }) => {
        const [tab, setTab] = useState(0);
        const [jsonCode, setJsonCode] = useState('');
        const [template, setTemplate] = useState<string>();

        const json = useMemo(() => {
            try {
                return JSON.parse(props.code) as Record<string, unknown>;
            } catch {
                return null;
            }
        }, [props.code]);

        useEffect(() => {
            setJsonCode(js_beautify(props.code));
        }, [props.code]);

        useEffect(() => {
            if (props.jinjaField && typeof json?.[props.jinjaField] === 'string') {
                setTemplate(json[props.jinjaField] as string);
            }
        }, [json, props.jinjaField]);

        const onSave = useCallback(() => {
            props.onChange?.({
                target: { name: props.name, value: jsonCode },
            } as unknown as ChangeEvent<HTMLInputElement>);
            props.onClose();
        }, [props.name, jsonCode, template, props.onClose]);

        const onTab = useCallback((_: SyntheticEvent, index: number) => {
            setTab(index);
        }, []);

        const onJinjaChange = useCallback(
            (code: string) => {
                const json = JSON.parse(jsonCode) as Record<string, unknown>;
                json[props.jinjaField!] = code;
                setJsonCode(js_beautify(JSON.stringify(json)));
                setTemplate(code);
            },
            [jsonCode, props.jinjaField],
        );

        return (
            <BaseModal open={props.open} maxWidth="xl" fullHeight onOk={onSave} onClose={props.onClose}>
                <Box display="flex" flexDirection="column" height={1} pb={0.5}>
                    <Tabs value={tab} onChange={onTab}>
                        <Tab label={props.title}></Tab>
                        {typeof template === 'string' && <Tab label="Value"></Tab>}
                    </Tabs>
                    {props.loading ? (
                        <CodeEditor code="loading..." language="jinja2" />
                    ) : (
                        <>
                            {tab === 0 && (
                                <Suspense>
                                    <CodeEditor code={jsonCode} language="json" onChange={setJsonCode} />
                                </Suspense>
                            )}
                            {tab === 1 && (
                                <Box flex={1}>
                                    <TemplateEditorModeProvider>
                                        <TemplateEditor template={template} onChange={onJinjaChange} />
                                    </TemplateEditorModeProvider>
                                </Box>
                            )}
                        </>
                    )}
                </Box>
            </BaseModal>
        );
    },
);

CodeEditorModal.displayName = 'CodeEditorModal';

const JsonTextField = memo(
    (
        props: TextFieldProps & {
            jinjaField?: string;
            labelItems?: ReactNode;
            editStringAsJinja?: boolean;
        },
    ) => {
        const [open, setOpen] = useState(false);
        const [input, setInput] = useState<HTMLInputElement>();

        const onChange = useCallback(
            (e: ChangeEvent<HTMLInputElement>) => {
                input!.value = e.target.value;
                props.inputProps?.onChange?.(e);
            },
            [input, props.inputProps],
        );

        const { jinjaField, labelItems, editStringAsJinja, ...rest } = props;

        return (
            <Box position="relative">
                <TextField
                    {...rest}
                    margin="dense"
                    fullWidth
                    variant="standard"
                    inputProps={{
                        ...props.inputProps,
                        ref: (input: HTMLInputElement) => {
                            props.inputProps?.ref(input);
                            setInput(input);
                        },
                        jinjaFields: editStringAsJinja,
                        onChange,
                    }}
                    label={
                        <Box display="flex">
                            <Typography mr={1}>{props.label}</Typography>
                            {props.labelItems}
                            {/*{(props.labelButtons || []).map(labelButton => (*/}
                            {/*    <Button disableRipple onClick={labelButton.onClick} sx={{ p: 0 }}>*/}
                            {/*        {labelButton.label}*/}
                            {/*    </Button>*/}
                            {/*))}*/}
                        </Box>
                    }
                    InputProps={{
                        inputComponent: JsonEditor,
                        disabled: props.disabled,
                    }}
                />
                <ExpandButton onClick={() => setOpen(true)} />
                <CodeEditorModal
                    open={open}
                    code={input?.value || ''}
                    name={props.inputProps?.name}
                    title={props.label as string}
                    onClose={() => setOpen(false)}
                    onChange={onChange}
                />
            </Box>
        );
    },
);

JsonTextField.displayName = 'JsonTextField';

const SchemaModal = (props: { open: boolean; onClose: () => void; dtype?: string }) => {
    const { data: schemaObj, isLoading } = useArgumentSchemaQuery(props.dtype);
    const schema = useMemo(() => (schemaObj ? js_beautify(JSON.stringify(schemaObj)) : null), [schemaObj]);

    return (
        <CodeEditorModal
            open={props.open}
            title="Params Schema"
            loading={isLoading}
            code={schema || ''}
            onClose={props.onClose}
        />
    );
};

const defaultDynamicCategories = {
    base_arg_md_id: 0,
    jsonpath: '',
    label: '',
    group_by_arg_md_id: null,
};

function ArgMetadataEditDialog({
    argMetadata,
    onSubmit,
    onClose,
}: {
    argMetadata: Partial<IArgumentMetadata>;
    onSubmit: (arg: IArgumentMetadata) => Promise<unknown>;
    onClose: () => void;
}) {
    const { data: extractors } = useArgExtractorsQuery();
    const argsMetadataQuery = useArgumentsMetadataQuery();
    const argNames = useMemo(
        () => Object.fromEntries(argsMetadataQuery.data?.map((arg) => [arg.name, arg.id]) || []),
        [argsMetadataQuery.data],
    );
    const extractorNames = useMemo(() => extractors?.map((e) => e.name), [extractors]);
    const extractor = extractors?.find((e) => e.name === argMetadata.extractor_name);

    const argDTypeHints = useDTypeHintsQuery();
    const [dialogValue, setDialogValue] = useState(argMetadata);
    const [saving, setSaving] = useState(false);
    const [showDynCat, setShowDynCat] = useState(!!argMetadata.options?.dynamic_categories);
    const [schemaOpen, setSchemaOpen] = useState(false);

    const {
        register,
        formState: { errors, isValid, defaultValues },
        setValue,
        getValues,
        handleSubmit,
        control,
        getFieldState,
        setError,
        trigger,
        watch,
    } = useForm<FormInput>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            ...argMetadata,
            options: argMetadata.options
                ? {
                      categories: js_beautify(JSON.stringify(argMetadata.options.categories || {})),
                      dynamic_categories: js_beautify(
                          JSON.stringify(argMetadata.options.dynamic_categories || defaultDynamicCategories),
                      ),
                      neitherable: argMetadata.options.neitherable,
                      group_by_label: argMetadata.options.group_by_label,
                  }
                : {
                      categories: '{}',
                      dynamic_categories: js_beautify(JSON.stringify(defaultDynamicCategories)),
                      neitherable: false,
                      group_by_label: false,
                  },
            params: argMetadata.params && js_beautify(JSON.stringify(argMetadata.params)),
            threshold: argMetadata.params?.threshold as number | undefined,
            dtype: argMetadata.dtype ?? 'str',
            arg_type: argMetadata.arg_type ?? ArgTypes[0],
            scope: argMetadata.scope ?? ArgScopes[0],
            additional_data: {
                ...argMetadata.additional_data,
            },
        },
    });
    const paramsValue = watch('params');
    let mayAsk = false;
    try {
        if (paramsValue) {
            const parsed = JSON.parse(paramsValue) as Record<string, any>;
            mayAsk = !!parsed.may_ask;
        }
    } catch {
        mayAsk = false;
    }

    const { data: llmExperimentSchema } = useLlmExperimentSchemaQuery();

    const [isCategoriesTouched, setIsCategoriesTouched] = useState(!!argMetadata.options?.categories);

    const dtypePrimitive = useMemo(() => {
        const dtype = getValues('dtype');
        const match = dtype.match(/list\[(.+)\]/);
        return match ? match[1] : dtype;
    }, [getValues('dtype')]);

    const { data: dtypeSchema, isLoading: dtypeSchemaIsLoading } = useArgumentSchemaQuery(dtypePrimitive);

    useLayoutEffect(() => {
        if (!isCategoriesTouched) {
            setValue(
                'options.categories',
                dtypeSchema?.enum
                    ? JSON.stringify(Object.fromEntries(dtypeSchema.enum.map((key) => [key, key])), null, 2)
                    : '{}',
                { shouldValidate: true },
            );
        }
    }, [dtypeSchema, isCategoriesTouched]);

    const onSubmitHandler: SubmitHandler<FormInput> = (values: FormInput) => {
        if (!isValid) return;
        if (values.name in argNames && (!argMetadata.id || argNames[values.name] !== argMetadata.id)) {
            setError('name', { type: 'unique', message: 'Name must be unique' });
            return;
        }
        if (values.arg_type === 'TEXT_ARGUMENT' && !values.threshold && values.threshold !== 0) {
            setError('threshold', { type: 'required', message: 'Threshold is required for text argument.' });
            return;
        }

        const params = (values.params ? (JSON.parse(values.params) as any) : dialogValue.params) || {};
        if (values.threshold !== undefined && values.arg_type === 'TEXT_ARGUMENT') {
            params.threshold = values.threshold;
        } else {
            delete params.threshold;
        }
        if (typeof params.may_ask !== 'undefined') {
            params.may_ask = !!params.may_ask;
        }
        if (!params.ask_instructions) {
            delete params.ask_instructions;
        }

        const parsed = {
            params,
            options: values.options
                ? {
                      ...dialogValue.options,
                      categories: showDynCat
                          ? null
                          : values.options.categories
                            ? JSON.parse(values.options.categories)
                            : {},
                      dynamic_categories: showDynCat
                          ? values.options?.dynamic_categories
                              ? JSON.parse(values.options?.dynamic_categories)
                              : {}
                          : null,
                      neitherable: values.options.neitherable,
                      group_by_label: values.options.group_by_label,
                  }
                : dialogValue.options,
            additional_data: {
                ...dialogValue.additional_data,
                ...values.additional_data,
                cache_missing_value: getFieldState('additional_data.cache_missing_value').isTouched
                    ? values.additional_data?.cache_missing_value
                    : dialogValue.additional_data?.cache_missing_value,
                ...(values.additional_data?.show_description !== undefined
                    ? { show_description: values.additional_data?.show_description }
                    : {}),
            },
        };
        setSaving(true);
        onSubmit({ ...dialogValue, ...values, ...parsed } as IArgumentMetadata).finally(() => setSaving(false));
    };

    const [experimentArgMd, setExperimentArgMd] = useState<IArgumentMetadata>();
    const createArgumentMetadata = useCreateArgumentMetadataMutation();
    const [isOpeningExperimentModal, setIsOpeningExperimentModal] = useState(false);
    const openPromptExperimentModal = async () => {
        try {
            setIsOpeningExperimentModal(true);
            const argMdClone = await createArgumentMetadata.mutateAsync({
                metadata: {
                    ...omit(argMetadata, ['id']),
                    deleted_at: new Date(),
                } as IArgumentMetadata,
            });
            setExperimentArgMd(argMdClone);
        } finally {
            setIsOpeningExperimentModal(false);
        }
    };

    const lastTouchedBy = String(
        argMetadata.additional_data?.updated_by || argMetadata.additional_data?.created_by || '',
    );

    const footer = useMemo(
        () => (
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                }}
            >
                {argMetadata.id && (
                    <Typography display="inline-block" variant="body1" fontWeight="bold">
                        {argMetadata.id?.toString()}
                    </Typography>
                )}
                <Typography sx={{ m: 0 }} variant="body2">
                    {argMetadata.deleted_at ? 'Deleted' : argMetadata.updated_at ? 'Updated' : 'Created'}
                    {lastTouchedBy && (
                        <>
                            {' '}
                            by <b>{lastTouchedBy}</b>
                        </>
                    )}
                    <>
                        {' '}
                        at{' '}
                        {new Date(
                            argMetadata.deleted_at || argMetadata.updated_at || argMetadata.created_date!,
                        ).toUTCString()}
                    </>
                </Typography>
            </Box>
        ),
        [argMetadata],
    );

    const canRunExperiment = useMemo(() => {
        const md = argMetadata;
        return (
            md.arg_type &&
            md.extractor_name &&
            md.arg_type === 'TEXT_ARGUMENT' &&
            ['llm', 'order_info_llm', 'no_agg_llm'].includes(md.extractor_name)
        );
    }, [argMetadata]);

    return (
        <BaseModal
            open={!!argMetadata}
            onClose={onClose}
            title={
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 3,
                    }}
                >
                    <span>{argMetadata.id ? 'Edit argument metadata' : 'Create a new argument'}</span>
                </Box>
            }
            loading={saving}
            maxWidth="lg"
            onOk={handleSubmit(onSubmitHandler)}
            closable={!saving}
            footer={footer}
        >
            <form noValidate onKeyDown={stopProgagation}>
                <TextField
                    required
                    fullWidth
                    autoFocus
                    margin="dense"
                    label="name"
                    type="text"
                    variant="standard"
                    error={!!errors.name}
                    helperText={errors.name?.message || ''}
                    {...register('name')}
                />
                <TextField
                    required
                    fullWidth
                    autoFocus
                    margin="dense"
                    label="title"
                    type="text"
                    variant="standard"
                    error={!!errors.title}
                    helperText={errors.title?.message || ''}
                    {...register('title')}
                />
                <TextField
                    required
                    fullWidth
                    margin="dense"
                    label="description"
                    type="text*"
                    variant="standard"
                    error={!!errors.description}
                    helperText={errors.description?.message || ''}
                    sx={{ mb: 1 }}
                    InputProps={{
                        endAdornment: canRunExperiment && (
                            <Button
                                onClick={() => {
                                    openPromptExperimentModal();
                                }}
                                sx={{ whiteSpace: 'nowrap' }}
                                disabled={isOpeningExperimentModal}
                            >
                                <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center' }}>
                                    {isOpeningExperimentModal && (
                                        <CircularProgress size={16} color="inherit" sx={{ mt: '-2px' }} />
                                    )}
                                    Open in editor
                                </Box>
                            </Button>
                        ),
                    }}
                    {...register('description')}
                />
                <FormGroup row sx={{ mt: 3, mb: 1.5, d: 'flex', justifyContent: 'space-between' }}>
                    <Box>
                        <FormControlLabel
                            label="Unspecifiable"
                            control={
                                <Switch defaultChecked={defaultValues?.unspecifiable} {...register('unspecifiable')} />
                            }
                        />
                        {isCategoricalMetadata({ options: getValues().options as IArgumentMetadata['options'] }) && (
                            <FormControlLabel
                                label="Neitherable"
                                control={
                                    <Switch
                                        defaultChecked={dialogValue.options?.neitherable}
                                        {...register('options.neitherable')}
                                    />
                                }
                            />
                        )}
                        <FormControlLabel
                            label="Unknowable"
                            control={<Switch defaultChecked={defaultValues?.unknowable} {...register('unknowable')} />}
                        />
                        <TextField
                            required={getValues('arg_type') === 'TEXT_ARGUMENT'}
                            autoFocus
                            margin="none"
                            label="Threshold"
                            disabled={getValues('arg_type') !== 'TEXT_ARGUMENT'}
                            type="number"
                            variant="standard"
                            defaultValue={getValues('arg_type') === 'TEXT_ARGUMENT' ? 2 : undefined}
                            error={!!errors.threshold}
                            helperText={errors.threshold?.message || ''}
                            sx={{
                                m: 0,
                                mt: -1.5,
                            }}
                            {...register('threshold', {
                                // Current solution for optional number input:
                                // https://github.com/orgs/react-hook-form/discussions/6980#discussioncomment-1785009
                                setValueAs: (v) => (v === '' ? undefined : parseFloat(v)),
                            })}
                        />
                    </Box>
                    <FormControlLabel
                        label="Always show description"
                        control={
                            <Switch
                                defaultChecked={defaultValues?.additional_data?.show_description}
                                {...register('additional_data.show_description')}
                            />
                        }
                    />
                </FormGroup>
                <Controller
                    control={control}
                    name="extractor_name"
                    defaultValue={defaultValues?.extractor_name}
                    render={({ field: { onChange, ...props }, fieldState: { error } }) => (
                        <Autocomplete
                            size="small"
                            fullWidth
                            options={uniq(extractorNames ?? []).sort()}
                            onChange={(_, extractorName) => {
                                onChange(extractorName);
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    error={Boolean(error)}
                                    helperText={error?.message || ''}
                                    label="Arg extractor*"
                                />
                            )}
                            loading={!extractorNames}
                            {...props}
                            value={props.value || null}
                            sx={{ mb: 1 }}
                        />
                    )}
                />
                <FormGroup row sx={{ mb: 1, justifyContent: 'space-between' }}>
                    <TextField
                        sx={{ width: '32%' }}
                        margin="dense"
                        size="small"
                        select
                        label="Argument Type"
                        required
                        defaultValue={argMetadata.arg_type}
                        onChange={() => trigger('arg_type')}
                        inputProps={register('arg_type')}
                    >
                        {ArgTypes.map((argType) => (
                            <MenuItem value={argType} key={argType}>
                                {argType}
                            </MenuItem>
                        ))}
                    </TextField>
                    <Controller
                        control={control}
                        name="dtype"
                        defaultValue={defaultValues?.dtype}
                        render={({ field: { onChange, ...props }, fieldState: { error } }) => (
                            <Autocomplete
                                size="small"
                                freeSolo
                                options={uniq(argDTypeHints.data ?? []).sort()}
                                onChange={(_, newValue) => {
                                    newValue && setDialogValue({ ...dialogValue, dtype: newValue });
                                    onChange(newValue);
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        error={Boolean(error)}
                                        helperText={error?.message || ''}
                                        onChange={(e) => {
                                            onChange(e.target.value);
                                        }}
                                        label={
                                            <Box display="flex">
                                                <Typography mr={1}>DType *</Typography>
                                                <Button sx={{ p: 0 }} disableRipple onClick={() => setSchemaOpen(true)}>
                                                    Schema
                                                </Button>
                                            </Box>
                                        }
                                    />
                                )}
                                loading={argDTypeHints.isLoading}
                                disabled={argDTypeHints.isLoading}
                                {...props}
                                value={props.value || null}
                                sx={{ mt: 1, width: '32%' }}
                            />
                        )}
                    />
                    <TextField
                        sx={{ width: '32%' }}
                        margin="dense"
                        size="small"
                        select
                        label="Scope"
                        required
                        defaultValue={argMetadata.scope}
                        inputProps={register('scope')}
                    >
                        {ArgScopes.map((scope) => (
                            <MenuItem value={scope} key={scope}>
                                {scope}
                            </MenuItem>
                        ))}
                    </TextField>
                </FormGroup>
                <FormGroup row sx={{ mb: 1, alignItems: 'center' }}>
                    <FormControlLabel
                        label="May ask"
                        control={
                            <Switch
                                checked={mayAsk}
                                onChange={(_, checked) => {
                                    const params = safeJsonParse(getValues('params'), {});
                                    params.may_ask = checked;
                                    setValue('params', JSON.stringify(params, null, 4), { shouldValidate: true });
                                }}
                            />
                        }
                        sx={{ width: '49%', m: 0 }}
                    />
                </FormGroup>
                {mayAsk && (
                    <TextField
                        label="Add instructions"
                        placeholder="Add instructions"
                        multiline
                        minRows={2}
                        maxRows={6}
                        fullWidth
                        margin="dense"
                        value={(() => {
                            const params = safeJsonParse(getValues('params'), {});
                            return params.ask_instructions || '';
                        })()}
                        onChange={(e) => {
                            const params = safeJsonParse(getValues('params'), {});
                            params.ask_instructions = e.target.value;
                            setValue('params', JSON.stringify(params, null, 4), { shouldValidate: true });
                        }}
                        slotProps={{
                            input: {
                                startAdornment: (
                                    <EditPromptIconButton
                                        value={(() => {
                                            const params = safeJsonParse(getValues('params'), {});
                                            return params.ask_instructions || '';
                                        })()}
                                        setValue={(value) => {
                                            const params = safeJsonParse(getValues('params'), {});
                                            params.ask_instructions = value;
                                            setValue('params', JSON.stringify(params, null, 4), {
                                                shouldValidate: true,
                                            });
                                        }}
                                    />
                                ),
                            },
                        }}
                        sx={{ mb: 2 }}
                    />
                )}
                <FormGroup row sx={{ mb: 1, alignItems: 'center' }}>
                    <TextField
                        label="Revoke after"
                        InputLabelProps={{ shrink: true }}
                        variant="outlined"
                        margin="dense"
                        error={!!errors.additional_data?.ttl_minutes}
                        helperText={errors.additional_data?.ttl_minutes?.message}
                        type="number"
                        inputProps={register('additional_data.ttl_minutes')}
                        InputProps={{
                            placeholder: `Default (${extractor?.ttl_minutes ?? 'none'}${
                                extractor?.ttl_minutes ? 'm' : ''
                            })`,
                            endAdornment: 'mins',
                            sx: { input: { pr: 1 } },
                        }}
                        sx={{ width: '49%' }}
                        size="small"
                        FormHelperTextProps={{ sx: { ml: 0 } }}
                    />
                    <FormControlLabel
                        label="Cache empty result"
                        sx={{ width: '49%', m: 0 }}
                        control={
                            <Switch
                                defaultChecked={defaultValues?.additional_data?.cache_missing_value ?? true}
                                {...register('additional_data.cache_missing_value')}
                            />
                        }
                    />
                </FormGroup>
                {!showDynCat && (
                    <JsonTextField
                        label="Categories"
                        labelItems={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {!isCategoriesTouched && dtypeSchemaIsLoading ? (
                                    <CircularProgress size={16} />
                                ) : (
                                    <>
                                        <Button sx={{ p: 0 }} disableRipple onClick={() => setShowDynCat(!showDynCat)}>
                                            Dynamic
                                        </Button>
                                        {isCategoriesTouched && (
                                            <Button
                                                onClick={() => {
                                                    setIsCategoriesTouched(false);
                                                    setValue('options.categories', '{}');
                                                }}
                                                color="secondary"
                                                sx={{ p: 0 }}
                                            >
                                                Reset
                                            </Button>
                                        )}
                                    </>
                                )}
                            </Box>
                        }
                        inputProps={register('options.categories')}
                        onChange={() => setIsCategoriesTouched(true)}
                        disabled={dtypeSchemaIsLoading && !isCategoriesTouched}
                        helperText={errors.options?.categories?.message}
                        error={!!errors.options?.categories}
                    />
                )}
                {showDynCat && (
                    <JsonTextField
                        label="Dynamic categories"
                        labelItems={
                            <Button sx={{ p: 0 }} disableRipple onClick={() => setShowDynCat(!showDynCat)}>
                                Static
                            </Button>
                        }
                        inputProps={register('options.dynamic_categories')}
                        helperText={errors.options?.dynamic_categories?.message}
                        error={!!errors.options?.dynamic_categories}
                    />
                )}
                <JsonTextField
                    label="Params"
                    error={!!errors.params}
                    helperText={errors.params?.message}
                    inputProps={register('params')}
                    jinjaField="value"
                    editStringAsJinja
                    sx={{ mb: 1 }}
                />
                {schemaOpen && (
                    <SchemaModal open={schemaOpen} onClose={() => setSchemaOpen(false)} dtype={dialogValue.dtype} />
                )}
                {experimentArgMd && llmExperimentSchema && (
                    <PromptExperimentModal
                        argMetadata={experimentArgMd}
                        llmExperimentSchema={llmExperimentSchema}
                        onClose={() => setExperimentArgMd(undefined)}
                        onSubmit={async (description) => {
                            setValue(
                                'params',
                                JSON.stringify(
                                    {
                                        ...(safeJsonParse(getValues('params'), {}) || {}),
                                        prompt_override: description,
                                    },
                                    null,
                                    4,
                                ),
                            );
                            setExperimentArgMd(undefined);
                        }}
                    />
                )}
            </form>
        </BaseModal>
    );
}

export default ArgMetadataEditDialog;
